import type { FeatureCollection } from "geojson";
import type { ImportTableColumn } from "@domain/savedView";
import type { Job } from "./Job";

/** Keys on Job.files that hold GeoJSON */
export type GeoJSONFileKey =
	| "boundaryGeoJSON"
	| "zonesGeoJSON"
	| "pointsGeoJSON"
	| "centroidsGeoJSON"
	| "coresGeoJSON"
	| "pathGeoJSON";

/** A single atomic edit to a job's geo data */
export interface JobEdit {
	jobId: string;
	fileKey: GeoJSONFileKey;
	oldValue: FeatureCollection | null;
	newValue: FeatureCollection | null;
}

/**
 * Readonly fields on Job that are computed by Airtable (formulas, lookups, rollups).
 * These are populated by the API and should never be written by the frontend.
 */
export type JobReadonlyField =
	| "name"
	| "edits"
	| "status"
	| "statusNumber"
	| "fieldName"
	| "testPackage"
	| "season"
	| "depth"
	| "density"
	| "frequencyOfMicros"
	| "labName"
	| "labAccountNumber"
	| "companyName"
	| "farmName"
	| "growerName"
	| "attachments";

/** Writable fields on Job — the subset that can be modified by the frontend */
export type WritableJob = Omit<Job, JobReadonlyField>;

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// Maintain the same order in all places for consistency.
// ==========================================================================================
export const columnDisplayNames: Record<ImportTableColumn, string> = {
	// Basic Information
	grower: "Grower Name",
	farm: "Farm Name",
	field: "Field Name",

	// Attachments
	boundaryGeoJSON: "Boundary File",
	pointsGeoJSON: "Points File",
	zonesGeoJSON: "Zone File",
	fieldNotes: "Field Notes",

	// Order
	isReadyToSample: "Ready to Sample",
	dateFieldReadyEstimated: "Est Field Ready Date",
	fieldPriority: "Field Priority",
	collectBoundary: "Record HD Boundary",
	submissionNotes: "Submission Notes",

	// Deal
	density: "Density",
	depth: "Depth",
	labName: "Lab Name",
	testPackage: "Test Package",
	frequencyOfMicros: "Frequency of Micros",
	deal: "Deal Configuration",

	// Lab Info
	labInstructions: "Instructions for Lab (not for Rogo)",
	microsList: "Samples with Addon Tests",
	lastCrop: "Last Crop",
	nextCrop: "Next Crop",
	yieldGoal: "Yield Goal",

	// Contacts
	branch: "Branch",
	salesperson: "Salesperson",
	salespersonMobilePhone: "Salesperson Mobile Phone",
	growerPhoneNumber: "Grower Phone Number",
	submitterName: "Submitter Name",
	submitterEmail: "Submitter Email",

	// Sync Codes
	eventId: "Event/Sync Code",
	labSubmissionCode: "Lab Submission Code",
	fieldId: "Field ID",
	externalId: "External ID",
	externalFarmId: "External Farm ID",
	externalLabId: "External Lab ID",

	// Misc
	isTestJob: "Test Job",
};

export interface CSVImportRows {
	[name: string]: unknown;
	growerName: string;
	growerEmail?: string;
	growerCell?: string;
	fieldName: string;
	fieldID?: string;
	farmName?: string;
	depth?: string;
	labName?: string;
	locationFromForm: string;
	gridSize?: string;
	testPackage?: string;
	ready?: string;
	fieldPriority?: string;
	syncCode: string;
	submissionNotes?: string;
	labAccountNumber?: string;
	freqOfMicros?: string;
	submitterName?: string;
	submitterEmail?: string;
	pointsAttached?: string;
	jobFlags: "Bulk Upload" | "Bulk Upload - Completed";
	boundaryChange: "Major" | "Minor" | undefined;
	specialLabInstructions: string;
	lastCrop: string;
	nextCrop: string;
	specsFromForm: string;
	regularLabSubmissionCode: string;
}
